import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { State } from 'redux/initialState';
import { HoldingClass, Operation, Society, Transaction } from 'types';

import { getTransactionAmount } from 'modules/transactions/pages/OperationDetails/utils/getTransactionAmount';
import { getHoldingClassFromTransaction } from 'modules/transactions/utils/getHoldingClassFromTransaction';
import { getTransactionNominalValue } from 'modules/transactions/utils/getTransactionNominalValue';
import { getTransactionSharesCount } from 'modules/transactions/utils/getTransactionSharesCount';

import { getTransactionTypeLabelKey } from '../utils/getTransactionTypeLabelKey';

type UseGetTransactionDataProps = {
  transaction: Transaction;
  operation: Operation;
  operationType: string;
};

export const useGetTransactionData = ({
  transaction,
  operation,
  operationType,
}: UseGetTransactionDataProps) => {
  const actualSociety = useSelector(
    (state: State) => state.society.actualSociety
  ) as Society | undefined;

  const data = useMemo(() => {
    if (!transaction) {
      return {
        type: '',
        className: '',
        from: 0,
        to: 0,
        sharesCount: 0,
        premium: 0,
        amount: 0,
        comments: '',
      };
    }

    const type = getTransactionTypeLabelKey({
      transactionType: transaction.transactionType,
      operationType,
    });

    const className = (transaction.shareClass as HoldingClass)?.name || '';
    const from = transaction.shareFrom || 0;
    const to = transaction.shareTo || 0;
    const sharesCount = getTransactionSharesCount(transaction);
    const society = actualSociety ?? ({} as Society);
    const nominalValue = getTransactionNominalValue({
      holdingClass: getHoldingClassFromTransaction(transaction),
      transaction,
      operation,
      society,
    });
    const amount = getTransactionAmount({
      transaction,
      sharesCount,
      nominalValue,
    });
    const premium = transaction?.sharePremium || 0;

    return {
      type,
      className,
      from,
      to,
      sharesCount,
      premium,
      amount,
      comments: transaction.comments || '',
    };
  }, [transaction, operation, operationType, actualSociety]);

  return {
    data,
  };
};
